import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Modal, 
  Steps, 
  Form, 
  Select, 
  DatePicker, 
  Input, 
  Button, 
  Space, 
  Table, 
  InputNumber,
  Typography,
  App,
  Divider,
  Card,
  Alert,
  Checkbox,
  Switch,
  Spin
} from 'antd';
import { PlusOutlined, DeleteOutlined, LeftOutlined, RightOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { 
  EventWithCircuit, 
  CreateEventRequest, 
  Station, 
  Session, 
  RadarPeriod,
  RadarBbox 
} from '../../api/eventsService';
import eventsService from '../../api/eventsService';
import eventJobsService, { type EventJob } from '../../api/eventJobsService';
import circuitsService, { type Circuit } from '../../api/circuitsService';
import { SeasonSelector } from '../Common/SeasonSelector';
import { CircuitSelector } from '../Common/CircuitSelector';
import { 
  adjustTimestamp, 
  formatDateDisplay 
} from '../../utils/dateAdjustment';
import {
  generateWeatherMessageJobs,
  generateDailyForecastJobs,
  generateHourlyForecastJobs,
  generateMinutelyForecastJobs,
  generateBackupJobs,
  generatePipelineStatusCheckJobs,
  generateArchiveObservationsJobs,
  getJobCounts,
  type JobCounts
} from '../../utils/jobScheduleGenerator';
import { useAuth } from '../../hooks/useAuth';
import { DateTimeInput } from '../Common/DateTimeInput';
import { JobsTimeline } from '../JobsTimeline';
import '../JobsTimeline/JobsTimeline.css';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const { TextArea } = Input;
const { Title } = Typography;
const { RangePicker } = DatePicker;

interface CreateEventModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface StationTableRecord extends Station {
  key: string;
}

interface SessionTableRecord extends Session {
  key: string;
}

interface RadarPeriodTableRecord extends RadarPeriod {
  key: string;
}

// Simple bbox coordinates for UI (min/max lat/lon)
interface BboxCoordinates {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

// Convert GeoJSON Polygon to simple bbox coordinates
const bboxToCoordinates = (bbox: RadarBbox | null | undefined): BboxCoordinates | null => {
  if (!bbox || !bbox.coordinates || !bbox.coordinates[0] || bbox.coordinates[0].length < 4) {
    return null;
  }
  
  const coords = bbox.coordinates[0];
  // Extract min/max from the polygon coordinates
  const lons = coords.map(c => c[0]);
  const lats = coords.map(c => c[1]);
  
  return {
    minLon: Math.min(...lons),
    maxLon: Math.max(...lons),
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
  };
};

// Convert simple bbox coordinates to GeoJSON Polygon
const coordinatesToBbox = (coords: BboxCoordinates): RadarBbox => {
  return {
    type: 'Polygon',
    coordinates: [[
      [coords.minLon, coords.minLat],
      [coords.maxLon, coords.minLat],
      [coords.maxLon, coords.maxLat],
      [coords.minLon, coords.maxLat],
      [coords.minLon, coords.minLat], // Close the polygon
    ]],
  };
};

export const CreateEventModal: React.FC<CreateEventModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState<EventWithCircuit | null>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [availableEvents, setAvailableEvents] = useState<EventWithCircuit[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [templateSeason, setTemplateSeason] = useState<number | undefined>();
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [loadingCircuits, setLoadingCircuits] = useState(false);
  const { authState } = useAuth();
  const { message } = App.useApp();

  useEffect(() => {
    if (!open) return;
    const previousSeason = new Date().getFullYear() - 1;
    setTemplateSeason(previousSeason);
  }, [open]);

  useEffect(() => {
    if (!open || templateSeason === undefined) return;
    let cancelled = false;
    const fetchEvents = async () => {
      setLoadingEvents(true);
      try {
        const response = await eventsService.getAllEvents(templateSeason);
        if (!cancelled && response.success && response.data) {
          setAvailableEvents(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch events for template selection:', err);
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    };
    fetchEvents();
    return () => { cancelled = true; };
  }, [open, templateSeason]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fetchCircuits = async () => {
      setLoadingCircuits(true);
      try {
        const data = await circuitsService.getAllCircuits();
        if (!cancelled) setCircuits(data);
      } catch (err) {
        console.error('Failed to fetch circuits:', err);
      } finally {
        if (!cancelled) setLoadingCircuits(false);
      }
    };
    fetchCircuits();
    return () => { cancelled = true; };
  }, [open]);
  
  // Form state
  const [circuitId, setCircuitId] = useState<string>('');
  const [circuitGeojsonId, setCircuitGeojsonId] = useState<string>('');
  const [eventName, setEventName] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [overviewSummary, setOverviewSummary] = useState<string>('');
  const [isDemo, setIsDemo] = useState<boolean>(false);
  const [observationsEnabled, setObservationsEnabled] = useState<boolean>(true);
  const [stations, setStations] = useState<StationTableRecord[]>([]);
  const [sessions, setSessions] = useState<SessionTableRecord[]>([]);
  const [radarPeriods, setRadarPeriods] = useState<RadarPeriodTableRecord[]>([]);
  const [radarBbox, setRadarBbox] = useState<BboxCoordinates | null>(null);
  
  // Job scheduling state
  const [enableWeatherMessages, setEnableWeatherMessages] = useState(true);
  const [enableDailyForecast, setEnableDailyForecast] = useState(true);
  const [enableHourlyForecast, setEnableHourlyForecast] = useState(true);
  const [enableMinutelyForecast, setEnableMinutelyForecast] = useState(true);
  const [enableBackup, setEnableBackup] = useState(true);
  const [enablePipelineStatusChecks, setEnablePipelineStatusChecks] = useState(true);
  const [enableArchiveObservations, setEnableArchiveObservations] = useState(true);
  const [pipelineMonitorStart, setPipelineMonitorStart] = useState<Dayjs | null>(null);
  const [jobCounts, setJobCounts] = useState<JobCounts | null>(null);
  const [calculatingJobs, setCalculatingJobs] = useState(false);
  const [previewJobs, setPreviewJobs] = useState<EventJob[]>([]);
  const [isJobsPreviewModalOpen, setIsJobsPreviewModalOpen] = useState(false);
  const jobCalcTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Reset form when modal closes
  // Note: form.resetFields() is not called here because the Modal uses destroyOnHidden,
  // which destroys the Form component when closed. Calling resetFields on a disconnected
  // form causes a warning.
  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setSelectedEvent(null);
      setCircuitId('');
      setCircuitGeojsonId('');
      setEventName('');
      setDateRange(null);
      setOverviewSummary('');
      setIsDemo(false);
      setObservationsEnabled(true);
      setStations([]);
      setSessions([]);
      setRadarPeriods([]);
      setRadarBbox(null);
      setEnableWeatherMessages(true);
      setEnableDailyForecast(true);
      setEnableHourlyForecast(true);
      setEnableMinutelyForecast(true);
      setEnableBackup(true);
      setJobCounts(null);
      setCalculatingJobs(false);
      setPreviewJobs([]);
      setPipelineMonitorStart(null);
      setIsJobsPreviewModalOpen(false);
    }
  }, [open]);
  
  const resolveCircuitTimezone = useCallback(() => {
    if (selectedEvent?.circuit.time_zone) {
      return selectedEvent.circuit.time_zone;
    }
    return circuits.find(c => c.circuit_id === circuitId)?.time_zone || 'UTC';
  }, [selectedEvent, circuits, circuitId]);

  // Calculate job counts and preview jobs (deferred so loading spinner renders first)
  const calculateJobs = useCallback(() => {
    if (!dateRange || sessions.length === 0) {
      setJobCounts(null);
      setPreviewJobs([]);
      setCalculatingJobs(false);
      return;
    }

    const jobTimezone = resolveCircuitTimezone();
    const [startDate, endDate] = dateRange;

    const validSessions = sessions.filter(s =>
      s.session_name && s.session_name.trim() && s.start_time && dayjs(s.start_time).isValid()
    );

    if (validSessions.length === 0) {
      setJobCounts(null);
      setPreviewJobs([]);
      setCalculatingJobs(false);
      return;
    }

    const sortedSessions = [...validSessions].sort((a, b) =>
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
    const firstSession = sortedSessions[0];
    const lastSession = sortedSessions[sortedSessions.length - 1];

    if (!firstSession || !lastSession) {
      setJobCounts(null);
      setPreviewJobs([]);
      setCalculatingJobs(false);
      return;
    }

    const weatherJobs = enableWeatherMessages
      ? generateWeatherMessageJobs(validSessions, jobTimezone)
      : [];
    const dailyJobs = enableDailyForecast
      ? generateDailyForecastJobs(startDate.format('YYYY-MM-DD'), firstSession.start_time, jobTimezone, {
          includeHourlyPeriodDays: enableDailyForecast && enableHourlyForecast,
          lastSessionEnd: lastSession.end_time,
        })
      : [];
    const hourlyJobs = enableHourlyForecast
      ? generateHourlyForecastJobs(firstSession.start_time, lastSession.end_time, jobTimezone, {
          sessions: validSessions,
        })
      : [];
    const minutelyJobs = enableMinutelyForecast
      ? generateMinutelyForecastJobs(validSessions, jobTimezone)
      : [];
    const backupJobs = enableBackup
      ? generateBackupJobs(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'), jobTimezone)
      : [];
    const pipelineJobs = enablePipelineStatusChecks
      ? generatePipelineStatusCheckJobs(startDate.format('YYYY-MM-DD'), validSessions, jobTimezone, {
          monitorStartTime: pipelineMonitorStart?.utc().toISOString(),
        })
      : [];
    const archiveJobs = enableArchiveObservations
      ? generateArchiveObservationsJobs(endDate.format('YYYY-MM-DD'), jobTimezone)
      : [];

    setJobCounts(getJobCounts(weatherJobs, dailyJobs, hourlyJobs, minutelyJobs, backupJobs, pipelineJobs, archiveJobs));

    const allPreviewJobs: Array<import('../../api/eventJobsService').CreateEventJobRequest> = [
      ...weatherJobs, ...dailyJobs, ...hourlyJobs, ...minutelyJobs, ...backupJobs, ...pipelineJobs, ...archiveJobs,
    ];
    const now = new Date().toISOString();
    setPreviewJobs(allPreviewJobs.map((job, idx) => ({
      id: `preview-${idx}`,
      season_year: startDate.year(),
      circuit_id: circuitId,
      start_date: startDate.format('YYYY-MM-DD'),
      job_type: job.job_type,
      scheduled_at: job.scheduled_at,
      status: 'pending' as const,
      started_at: null,
      completed_at: null,
      error_message: null,
      job_config: job.job_config ?? null,
      created_at: now,
      created_by: '',
      updated_at: now,
    })));
    setCalculatingJobs(false);
  }, [dateRange, sessions, circuitId, resolveCircuitTimezone, enableWeatherMessages, enableDailyForecast, enableHourlyForecast, enableMinutelyForecast, enableBackup, enablePipelineStatusChecks, enableArchiveObservations, pipelineMonitorStart]);

  useEffect(() => {
    if (currentStep !== 6) return;

    setCalculatingJobs(true);
    clearTimeout(jobCalcTimeoutRef.current);
    // Defer so the spinner paints before the heavy computation runs
    jobCalcTimeoutRef.current = setTimeout(calculateJobs, 50);

    return () => clearTimeout(jobCalcTimeoutRef.current);
  }, [currentStep, calculateJobs]);

  // Handle event selection
  const handleEventSelect = (eventKey: string) => {
    const event = availableEvents.find(
      e => `${e.season_year}-${e.circuit_id}-${e.start_date}` === eventKey
    );
    
    if (event) {
      setSelectedEvent(event);
      
      // Auto-populate basic fields
      setCircuitId(event.circuit_id);
      setCircuitGeojsonId(event.circuit_geojson_id || '');
      setEventName(event.name);
      setOverviewSummary(event.overview_summary || '');
      
      // Populate stations
      const stationRecords: StationTableRecord[] = event.stations.map((s, idx) => ({
        ...s,
        key: `station-${idx}`,
      }));
      setStations(stationRecords);
      
      // Populate radar bbox from template
      const bboxCoords = bboxToCoordinates(event.radar_bbox);
      setRadarBbox(bboxCoords);
      
      // We'll populate sessions and radar periods when user proceeds to those steps
      form.setFieldsValue({
        eventTemplate: eventKey,
        circuitId: event.circuit_id,
        circuitGeojsonId: event.circuit_geojson_id || '',
        eventName: event.name,
        overviewSummary: event.overview_summary || '',
      });
    }
  };

  const handleClearTemplate = () => {
    setSelectedEvent(null);
    setCircuitId('');
    setCircuitGeojsonId('');
    setEventName('');
    setOverviewSummary('');
    setStations([]);
    setSessions([]);
    setRadarPeriods([]);
    setRadarBbox(null);
    form.setFieldsValue({
      eventTemplate: undefined,
      circuitId: undefined,
      circuitGeojsonId: undefined,
      eventName: undefined,
      overviewSummary: undefined,
    });
  };

  // Adjust sessions when dates change
  const adjustSessions = () => {
    if (!selectedEvent || !dateRange) return;
    
    const [newStartDate] = dateRange;
    const oldStartDate = selectedEvent.start_date;
    
    const adjustedSessions: SessionTableRecord[] = selectedEvent.sessions.map((session, idx) => ({
      session_name: session.session_name,
      start_time: adjustTimestamp(session.start_time, oldStartDate, newStartDate.format('YYYY-MM-DD')),
      end_time: adjustTimestamp(session.end_time, oldStartDate, newStartDate.format('YYYY-MM-DD')),
      description: session.description,
      key: `session-${idx}`,
    }));
    
    setSessions(adjustedSessions);
  };

  // Adjust radar periods when dates change
  const adjustRadarPeriods = () => {
    if (!selectedEvent || !dateRange) return;
    
    // Check if radar periods exist and have valid data
    if (!selectedEvent.radar_periods || selectedEvent.radar_periods.length === 0) {
      setRadarPeriods([]);
      return;
    }
    
    const [newStartDate] = dateRange;
    const oldStartDate = selectedEvent.start_date;
    
    try {
      const adjustedPeriods: RadarPeriodTableRecord[] = selectedEvent.radar_periods.map((period, idx) => {
        // Radar periods are stored as ISO timestamps, use adjustTimestamp (same as sessions)
        // Then convert to UTC format for storage
        const adjustedStartISO = adjustTimestamp(period.start_time, oldStartDate, newStartDate.format('YYYY-MM-DD'));
        const adjustedEndISO = adjustTimestamp(period.end_time, oldStartDate, newStartDate.format('YYYY-MM-DD'));
        
        // Convert ISO to UTC format for backend storage
        const adjustedStart = convertISOToUTCFormat(adjustedStartISO);
        const adjustedEnd = convertISOToUTCFormat(adjustedEndISO);
        
        return {
          start_time: adjustedStart,
          end_time: adjustedEnd,
          key: `radar-${idx}`,
        };
      });
      
      setRadarPeriods(adjustedPeriods);
    } catch (error) {
      console.error('Error adjusting radar periods:', error);
      // Set empty array if adjustment fails
      setRadarPeriods([]);
    }
  };

  // Helper to convert ISO format to UTC format for backend
  const convertISOToUTCFormat = (isoTimestamp: string): string => {
    const date = new Date(isoTimestamp);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    const ms = String(date.getUTCMilliseconds()).padStart(3, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${ms}000 UTC`;
  };

  // Handle step navigation
  const handleNext = async () => {
    try {
      // Validate current step
      if (currentStep === 1) {
        const values = await form.validateFields(['circuitId', 'eventName', 'dateRange']);
        if (values.circuitId) {
          setCircuitId(values.circuitId);
        }
        if (values.eventName) {
          setEventName(values.eventName);
        }

        // Copy and date-shift sessions/radar from the template when one was selected
        if (selectedEvent) {
          adjustSessions();
          adjustRadarPeriods();
        }
      }
      // Step 2 is overview summary - no validation needed
      
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  // Handle create event
  const handleCreate = async () => {
    const resolvedCircuitId = circuitId || form.getFieldValue('circuitId');
    const resolvedEventName = eventName || form.getFieldValue('eventName');
    if (!dateRange || !authState?.token || !resolvedCircuitId || !resolvedEventName) {
      message.error('Missing required information');
      return;
    }
    
    try {
      setLoading(true);
      
      const [startDate, endDate] = dateRange;
      
      // Derive season year from start date
      const seasonYear = startDate.year();
      
      const createRequest: CreateEventRequest = {
        season_year: seasonYear,
        circuit_id: resolvedCircuitId,
        name: resolvedEventName,
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
        legacy: false, // New events are not legacy
        demo: isDemo, // Set based on user selection
        observations_enabled: observationsEnabled,
        overview_summary: overviewSummary || null,
        circuit_geojson_id: circuitGeojsonId || null,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        stations: stations.map(({ key, ...station }) => station),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        sessions: sessions.map(({ key, ...session }) => session),
        radar_bbox: radarBbox ? coordinatesToBbox(radarBbox) : null,
        radar_periods: radarPeriods.length > 0 
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          ? radarPeriods.map(({ key, ...period }) => period) 
          : undefined,
      };
      
      const response = await eventsService.createEvent(createRequest, authState.token);
      
      if (response.success) {
        const eventType = isDemo ? 'demo event' : 'real event';
        message.success(`${eventType.charAt(0).toUpperCase() + eventType.slice(1)} created successfully!`);
        
        // Create jobs if any are enabled
        if (enableWeatherMessages || enableDailyForecast || enableHourlyForecast || enableMinutelyForecast || enableBackup || enablePipelineStatusChecks || enableArchiveObservations) {
          try {
            await createJobs(seasonYear, resolvedCircuitId, startDate.format('YYYY-MM-DD'));
          } catch (jobError) {
            console.error('Failed to create some jobs:', jobError);
            message.warning('Event created successfully, but some jobs failed to create. You can create them manually in the Jobs page.');
          }
        }
        
        onSuccess();
        onClose();
      } else {
        message.error('Failed to create event');
      }
    } catch (error) {
      console.error('Failed to create event:', error);
      message.error(error instanceof Error ? error.message : 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };
  
  // Create jobs for the event
  const createJobs = async (
    seasonYear: number, 
    circuitId: string, 
    startDate: string,
    onProgress?: (current: number, total: number) => void
  ) => {
    if (!dateRange || sessions.length === 0) {
      return;
    }
    
    const jobTimezone = resolveCircuitTimezone();
    const [, endDateObj] = dateRange;
    
    // Find first and last session
    const sortedSessions = [...sessions].sort((a, b) => 
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
    const firstSession = sortedSessions[0];
    const lastSession = sortedSessions[sortedSessions.length - 1];
    
    if (!firstSession || !lastSession) {
      return;
    }
    
    // Generate all job schedules
    const allJobs: Array<{ job: import('../../api/eventJobsService').CreateEventJobRequest; type: string }> = [];
    
    if (enableWeatherMessages) {
      const jobs = generateWeatherMessageJobs(sessions, jobTimezone);
      allJobs.push(...jobs.map(job => ({ job, type: 'weather_message' })));
    }
    
    if (enableDailyForecast) {
      const jobs = generateDailyForecastJobs(startDate, firstSession.start_time, jobTimezone, {
          includeHourlyPeriodDays: enableDailyForecast && enableHourlyForecast,
          lastSessionEnd: lastSession.end_time,
        });
      allJobs.push(...jobs.map(job => ({ job, type: 'daily_forecast' })));
    }
    
    if (enableHourlyForecast) {
      const jobs = generateHourlyForecastJobs(firstSession.start_time, lastSession.end_time, jobTimezone, {
        sessions,
      });
      allJobs.push(...jobs.map(job => ({ job, type: 'hourly_forecast' })));
    }

    if (enableMinutelyForecast) {
      const jobs = generateMinutelyForecastJobs(sessions, jobTimezone);
      allJobs.push(...jobs.map(job => ({ job, type: 'minutely_forecast' })));
    }
    
    if (enableBackup) {
      const jobs = generateBackupJobs(startDate, endDateObj.format('YYYY-MM-DD'), jobTimezone);
      allJobs.push(...jobs.map(job => ({ job, type: 'backup' })));
    }

    if (enablePipelineStatusChecks) {
      const jobs = generatePipelineStatusCheckJobs(startDate, sessions, jobTimezone, {
        monitorStartTime: pipelineMonitorStart?.utc().toISOString(),
      });
      allJobs.push(...jobs.map(job => ({ job, type: job.job_type })));
    }

    if (enableArchiveObservations) {
      const jobs = generateArchiveObservationsJobs(endDateObj.format('YYYY-MM-DD'), jobTimezone);
      allJobs.push(...jobs.map(job => ({ job, type: 'archive_observations' })));
    }
    
    // Filter out jobs scheduled in the past (backend validation requires future dates)
    const now = new Date();
    const validJobs = allJobs.filter(({ job }) => {
      const scheduledDate = new Date(job.scheduled_at);
      return scheduledDate > now;
    });
    
    const skippedCount = allJobs.length - validJobs.length;
    if (skippedCount > 0) {
      console.log(`Skipping ${skippedCount} job(s) scheduled in the past`);
    }
    
    if (validJobs.length === 0) {
      message.info('No jobs to create (all are scheduled in the past)');
      return;
    }

    // Show initial progress
    if (onProgress) {
      onProgress(0, validJobs.length);
    }
    message.loading({ content: `Creating ${validJobs.length} job${validJobs.length > 1 ? 's' : ''} in batch...`, key: 'creating-jobs', duration: 0 });
    
    // Extract just the job objects for batch creation
    const jobsToCreate = validJobs.map(({ job }) => job);
    
    try {
      // Create all jobs in a single batch operation
      const result = await eventJobsService.createJobsBatch(
        seasonYear,
        circuitId,
        startDate,
        jobsToCreate,
        isDemo
      );
      
      // Close loading message
      message.destroy('creating-jobs');
      
      if (result.success && result.data) {
        const created = result.data.created || (Array.isArray(result.data) ? result.data.length : 0);
        const skipped = result.data.skipped || 0;
        
        if (created > 0) {
          message.success(`Created ${created} job${created > 1 ? 's' : ''} successfully`);
        }
        if (skippedCount > 0 || skipped > 0) {
          const totalSkipped = skippedCount + skipped;
          message.info(`Skipped ${totalSkipped} job${totalSkipped > 1 ? 's' : ''} scheduled in the past or invalid`);
        }
        if (result.data.invalidJobs && result.data.invalidJobs.length > 0) {
          message.warning(`${result.data.invalidJobs.length} job${result.data.invalidJobs.length > 1 ? 's' : ''} failed validation`);
        }
      } else {
        const serverMessage = result.errorData?.message;
        message.error(serverMessage || 'Failed to create jobs');
      }
    } catch (error) {
      message.destroy('creating-jobs');
      console.error('Failed to create jobs:', error);
      message.error(error instanceof Error ? error.message : 'Failed to create jobs');
    }
  };

  // Timezone from the template event, or from the circuit chosen for a from-scratch event
  const circuitTimezone = resolveCircuitTimezone();

  // Helper function to parse UTC timestamp string to dayjs in circuit timezone
  // Handles multiple formats: "YYYY-MM-DD HH:mm:ss.SSSSSS UTC" and ISO 8601
  const parseUTCTimestamp = (utcTimestamp: string): Dayjs => {
    if (!utcTimestamp) {
      console.error('Empty UTC timestamp');
      return dayjs().tz(circuitTimezone);
    }
    
    try {
      // Try parsing as ISO 8601 format first (e.g., "2025-11-06T13:00:00.000Z")
      if (utcTimestamp.includes('T')) {
        const parsed = dayjs.utc(utcTimestamp);
        if (parsed.isValid()) {
          return parsed.tz(circuitTimezone);
        }
      }
      
      // Try parsing the "YYYY-MM-DD HH:mm:ss.SSSSSS UTC" format
      const cleanTimestamp = utcTimestamp.replace(' UTC', '').trim();
      const [datePart, timePart] = cleanTimestamp.split(' ');
      if (datePart && timePart) {
        const isoFormat = `${datePart}T${timePart.split('.')[0]}Z`;
        const parsed = dayjs.utc(isoFormat);
        if (parsed.isValid()) {
          return parsed.tz(circuitTimezone);
        }
      }
      
      // Fallback: try direct parsing
      const fallback = dayjs(utcTimestamp);
      if (fallback.isValid()) {
        return fallback.tz(circuitTimezone);
      }
      
      console.error('Could not parse UTC timestamp:', utcTimestamp);
      return dayjs().tz(circuitTimezone);
    } catch (error) {
      console.error('Error parsing UTC timestamp:', utcTimestamp, error);
      return dayjs().tz(circuitTimezone);
    }
  };

  // Helper function to format dayjs to UTC timestamp string
  const formatToUTCTimestamp = (date: Dayjs): string => {
    // Convert from circuit timezone to UTC and format as "YYYY-MM-DD HH:mm:ss.SSSSSS UTC"
    const utcDate = date.utc();
    const formatted = utcDate.format('YYYY-MM-DD HH:mm:ss.SSSSSS');
    return `${formatted} UTC`;
  };

  // Helper function to parse ISO timestamp to circuit timezone
  const parseISOToCircuitTZ = (isoTimestamp: string): Dayjs => {
    return dayjs(isoTimestamp).tz(circuitTimezone);
  };

  // Station columns
  const stationColumns: ColumnsType<StationTableRecord> = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 100,
      render: (val: string, _record, index) => (
        <Input
          value={val}
          onChange={(e) => {
            const newStations = [...stations];
            newStations[index].reference = e.target.value;
            setStations(newStations);
          }}
        />
      ),
    },
    {
      title: 'Latitude',
      dataIndex: 'lat',
      key: 'lat',
      render: (val: number, _record, index) => (
        <InputNumber
          value={val}
          onChange={(value) => {
            const newStations = [...stations];
            newStations[index].lat = value || 0;
            setStations(newStations);
          }}
          step={0.000001}
          precision={6}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Longitude',
      dataIndex: 'lon',
      key: 'lon',
      render: (val: number, _record, index) => (
        <InputNumber
          value={val}
          onChange={(value) => {
            const newStations = [...stations];
            newStations[index].lon = value || 0;
            setStations(newStations);
          }}
          step={0.000001}
          precision={6}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Altitude',
      dataIndex: 'altitude',
      key: 'altitude',
      render: (val: number, _record, index) => (
        <InputNumber
          value={val}
          onChange={(value) => {
            const newStations = [...stations];
            newStations[index].altitude = value || 0;
            setStations(newStations);
          }}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Direction',
      dataIndex: 'nearest_segment_direction',
      key: 'nearest_segment_direction',
      render: (val: number, _record, index) => (
        <InputNumber
          value={val}
          onChange={(value) => {
            const newStations = [...stations];
            newStations[index].nearest_segment_direction = value || 0;
            setStations(newStations);
          }}
          min={0}
          max={359}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_val, _record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveStation(index)}
          title="Remove station"
        />
      ),
    },
  ];

  // Session columns
  const sessionColumns: ColumnsType<SessionTableRecord> = [
    {
      title: 'Session Name',
      dataIndex: 'session_name',
      key: 'session_name',
      width: 150,
      render: (val: string, _record, index) => (
        <Input
          value={val}
          onChange={(e) => {
            const newSessions = [...sessions];
            newSessions[index].session_name = e.target.value;
            setSessions(newSessions);
          }}
        />
      ),
    },
    {
      title: `Start Time (${circuitTimezone})`,
      dataIndex: 'start_time',
      key: 'start_time',
      width: 200,
      render: (val: string, _record, index) => (
        <DateTimeInput
          value={parseISOToCircuitTZ(val).format('YYYY-MM-DD HH:mm:ss')}
          onChange={(formatted) => {
            const parsed = dayjs.tz(formatted.replace(' ', 'T'), circuitTimezone);
            if (parsed.isValid()) {
              const newSessions = [...sessions];
              newSessions[index].start_time = parsed.toISOString();
              setSessions(newSessions);
            }
          }}
        />
      ),
    },
    {
      title: `End Time (${circuitTimezone})`,
      dataIndex: 'end_time',
      key: 'end_time',
      width: 200,
      render: (val: string, _record, index) => (
        <DateTimeInput
          value={parseISOToCircuitTZ(val).format('YYYY-MM-DD HH:mm:ss')}
          onChange={(formatted) => {
            const parsed = dayjs.tz(formatted.replace(' ', 'T'), circuitTimezone);
            if (parsed.isValid()) {
              const newSessions = [...sessions];
              newSessions[index].end_time = parsed.toISOString();
              setSessions(newSessions);
            }
          }}
        />
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (val: string | null, _record, index) => (
        <Input
          value={val || ''}
          onChange={(e) => {
            const newSessions = [...sessions];
            newSessions[index].description = e.target.value || null;
            setSessions(newSessions);
          }}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_val, _record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveSession(index)}
          title="Remove session"
        />
      ),
    },
  ];

  // Handler functions for adding/removing rows
  const handleAddStation = () => {
    const newStation: StationTableRecord = {
      reference: `${stations.length + 1}`,
      lat: 0,
      lon: 0,
      altitude: 0,
      nearest_segment_direction: 0,
      key: `station-${Date.now()}`,
    };
    setStations([...stations, newStation]);
  };

  const handleRemoveStation = (index: number) => {
    const newStations = stations.filter((_, i) => i !== index);
    setStations(newStations);
  };

  const handleAddSession = () => {
    const now = dayjs().tz(circuitTimezone);
    const newSession: SessionTableRecord = {
      session_name: 'NEW SESSION',
      start_time: now.toISOString(),
      end_time: now.add(1, 'hour').toISOString(),
      description: null,
      key: `session-${Date.now()}`,
    };
    setSessions([...sessions, newSession]);
  };

  const handleRemoveSession = (index: number) => {
    const newSessions = sessions.filter((_, i) => i !== index);
    setSessions(newSessions);
  };

  const handleShiftSessions = (days: number) => {
    const shifted = sessions.map(session => ({
      ...session,
      start_time: dayjs(session.start_time).add(days, 'day').toISOString(),
      end_time: dayjs(session.end_time).add(days, 'day').toISOString(),
    }));
    setSessions(shifted);
  };

  const handleAddRadarPeriod = () => {
    const now = dayjs().tz(circuitTimezone);
    const newPeriod: RadarPeriodTableRecord = {
      start_time: formatToUTCTimestamp(now),
      end_time: formatToUTCTimestamp(now.add(1, 'hour')),
      key: `radar-${Date.now()}`,
    };
    setRadarPeriods([...radarPeriods, newPeriod]);
  };

  const handleRemoveRadarPeriod = (index: number) => {
    const newRadarPeriods = radarPeriods.filter((_, i) => i !== index);
    setRadarPeriods(newRadarPeriods);
  };

  // Radar period columns
  const radarPeriodColumns: ColumnsType<RadarPeriodTableRecord> = [
    {
      title: `Start Time (${circuitTimezone})`,
      dataIndex: 'start_time',
      key: 'start_time',
      width: 220,
      render: (val: string, _record, index) => (
        <DatePicker
          showTime
          value={parseUTCTimestamp(val)}
          onChange={(date) => {
            if (date) {
              const newRadarPeriods = [...radarPeriods];
              newRadarPeriods[index].start_time = formatToUTCTimestamp(date);
              setRadarPeriods(newRadarPeriods);
            }
          }}
          format="YYYY-MM-DD HH:mm:ss"
          showNow={false}
        />
      ),
    },
    {
      title: `End Time (${circuitTimezone})`,
      dataIndex: 'end_time',
      key: 'end_time',
      width: 220,
      render: (val: string, _record, index) => (
        <DatePicker
          showTime
          value={parseUTCTimestamp(val)}
          onChange={(date) => {
            if (date) {
              const newRadarPeriods = [...radarPeriods];
              newRadarPeriods[index].end_time = formatToUTCTimestamp(date);
              setRadarPeriods(newRadarPeriods);
            }
          }}
          format="YYYY-MM-DD HH:mm:ss"
          showNow={false}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_val, _record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveRadarPeriod(index)}
          title="Remove radar period"
        />
      ),
    },
  ];

  // Steps configuration
  const steps = [
    {
      title: 'Template',
      content: 'Optional template',
    },
    {
      title: 'Details',
      content: 'Event info',
    },
    {
      title: 'Overview',
      content: 'Summary',
    },
    {
      title: 'Sessions',
      content: 'Event sessions',
    },
    {
      title: 'Stations',
      content: 'Weather stations',
    },
    {
      title: 'Radar',
      content: 'Radar settings',
    },
    {
      title: 'Jobs',
      content: 'Schedule jobs',
    },
  ];

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Form form={form} layout="vertical">
            <Alert
              title="Optional template"
              description="You can duplicate an existing event to copy stations, sessions, and radar settings, or skip this step to create a blank event."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Form.Item label="Template Season">
              <SeasonSelector
                value={templateSeason}
                onChange={(year) => {
                  setTemplateSeason(year);
                  handleClearTemplate();
                }}
                style={{ width: '100%' }}
                placeholder="Select season to browse events"
              />
            </Form.Item>
            <Form.Item
              label="Select Event to Duplicate"
              name="eventTemplate"
            >
              <Select
                placeholder="Choose a previous event as template (optional)"
                onChange={(value: string | undefined) => {
                  if (value) {
                    handleEventSelect(value);
                  } else {
                    handleClearTemplate();
                  }
                }}
                allowClear
                showSearch
                optionFilterProp="children"
                size="large"
                loading={loadingEvents}
                notFoundContent={loadingEvents ? <Spin size="small" /> : undefined}
              >
                {availableEvents.map((event) => (
                  <Select.Option
                    key={`${event.season_year}-${event.circuit_id}-${event.start_date}`}
                    value={`${event.season_year}-${event.circuit_id}-${event.start_date}`}
                  >
                    {event.circuit.display_name} - {event.start_date}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            
            {selectedEvent ? (
              <Card size="small" style={{ marginTop: 16 }}>
                <Title level={5}>Template Event Details</Title>
                <p><strong>Season:</strong> {selectedEvent.season_year}</p>
                <p><strong>Circuit:</strong> {selectedEvent.circuit.display_name}</p>
                <p><strong>Dates:</strong> {formatDateDisplay(selectedEvent.start_date)} - {formatDateDisplay(selectedEvent.end_date)}</p>
                <p><strong>Stations:</strong> {selectedEvent.stations.length}</p>
                <p><strong>Sessions:</strong> {selectedEvent.sessions.length}</p>
                <p><strong>Radar Bbox:</strong> {selectedEvent.radar_bbox ? 'Configured' : 'Not set'}</p>
                <p><strong>Radar Periods:</strong> {selectedEvent.radar_periods?.length || 0}</p>
                <Alert
                  title="The new event will be created with data copied from this template. You can customize all fields in the next steps."
                  type="success"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              </Card>
            ) : (
              <Alert
                title="Creating from scratch"
                description="No template selected. You will choose a circuit and enter event details, sessions, stations, and radar settings in the next steps."
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </Form>
        );
      
      case 1:
        return (
          <Form form={form} layout="vertical">
            <Alert
              title="Event Details"
              description={selectedEvent
                ? "Configure the basic details for the new event. The season year will be automatically derived from the start date. Dates will be used to automatically adjust session and radar period times."
                : "Configure the basic details for the new event. The season year will be automatically derived from the start date. You will add sessions, stations, and radar settings in the following steps."}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form.Item
              label="Circuit"
              name="circuitId"
              rules={[{ required: true, message: selectedEvent ? 'Please enter circuit ID' : 'Please select a circuit' }]}
            >
              {selectedEvent ? (
                <Input
                  value={circuitId}
                  disabled
                />
              ) : (
                <CircuitSelector
                  circuits={circuits}
                  value={circuitId || null}
                  onChange={(id) => {
                    const nextId = id || '';
                    setCircuitId(nextId);
                    const circuit = circuits.find(c => c.circuit_id === nextId);
                    if (!eventName && circuit) {
                      setEventName(circuit.display_name);
                    }
                    form.setFieldsValue({
                      circuitId: nextId || undefined,
                      ...(!eventName && circuit ? { eventName: circuit.display_name } : {}),
                    });
                  }}
                  loading={loadingCircuits}
                  placeholder="Select a circuit"
                  style={{ width: '100%', minWidth: 0 }}
                />
              )}
            </Form.Item>
            
            <Form.Item
              label="Circuit GeoJSON ID"
              name="circuitGeojsonId"
              tooltip="Optional: Specify a custom GeoJSON file for this event's track layout. Leave empty to use the default circuit layout."
            >
              <Input
                value={circuitGeojsonId}
                onChange={(e) => setCircuitGeojsonId(e.target.value)}
                placeholder={`Leave empty to use "${circuitId}" layout`}
              />
            </Form.Item>
            
            <Form.Item
              label="Event Name"
              name="eventName"
              rules={[{ required: true, message: 'Please enter event name' }]}
            >
              <Input
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
              />
            </Form.Item>
            
            <Form.Item
              label="Event Date Range"
              name="dateRange"
              rules={[{ required: true, message: 'Please select date range' }]}
            >
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  } else {
                    setDateRange(null);
                  }
                }}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
              />
            </Form.Item>
            
            <Form.Item>
              <Checkbox
                checked={isDemo}
                onChange={(e) => setIsDemo(e.target.checked)}
              >
                <Space orientation="vertical" size={0}>
                  <span style={{ fontWeight: 500 }}>This is a demo event</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    Demo events use separate tables and can be easily cleaned up
                  </span>
                </Space>
              </Checkbox>
            </Form.Item>
          </Form>
        );
      
      case 2:
        return (
          <Form form={form} layout="vertical">
            <Alert
              title="Overview Summary"
              description="Add or edit the event overview and summary information. This is optional and can be updated later."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form.Item
              label="Overview Summary"
              name="overviewSummary"
            >
              <TextArea
                value={overviewSummary}
                onChange={(e) => setOverviewSummary(e.target.value)}
                rows={12}
                placeholder="Event overview and summary information..."
                style={{ fontSize: '14px' }}
              />
            </Form.Item>
          </Form>
        );
      
      case 3:
        return (
          <div>
            <Alert
              title="Event Sessions"
              description={selectedEvent
                ? `Sessions have been automatically adjusted based on the date difference. Times are displayed in ${circuitTimezone}. Original event: ${selectedEvent.start_date}, New event: ${dateRange?.[0].format('YYYY-MM-DD')}`
                : `Add sessions for this event. Times are displayed in ${circuitTimezone}.`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Space style={{ marginBottom: 12 }}>
              <Button
                icon={<LeftOutlined />}
                onClick={() => handleShiftSessions(-1)}
                disabled={sessions.length === 0}
              >
                1 Day Back
              </Button>
              <Button
                icon={<RightOutlined />}
                onClick={() => handleShiftSessions(1)}
                disabled={sessions.length === 0}
              >
                1 Day Forward
              </Button>
            </Space>
            <Table
              dataSource={sessions}
              columns={sessionColumns}
              pagination={false}
              scroll={{ x: 900 }}
              size="small"
              locale={{ emptyText: 'No sessions yet. Click the + button below to add one.' }}
            />
            <Button
              type="dashed"
              onClick={handleAddSession}
              style={{ width: '100%', marginTop: 16 }}
              icon={<PlusOutlined />}
            />
          </div>
        );
      
      case 4:
        return (
          <div>
            <Alert
              title="Weather Stations"
              description="Review and edit the weather station locations for this event. You can adjust coordinates, altitude, and direction."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Table
              dataSource={stations}
              columns={stationColumns}
              pagination={false}
              scroll={{ x: 850 }}
              size="small"
              locale={{ emptyText: 'No stations yet. Click the + button below to add one.' }}
            />
            <Button
              type="dashed"
              onClick={handleAddStation}
              style={{ width: '100%', marginTop: 16 }}
              icon={<PlusOutlined />}
            />
          </div>
        );
      
      case 5:
        return (
          <div>
            <Alert
              title="Radar Settings"
              description={`Configure radar bounding box and coverage periods. Radar bbox defines the geographic area for radar data. Times are displayed in ${circuitTimezone} but stored as UTC.`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <div style={{ display: 'flex', gap: 16 }}>
              {/* Radar Bounding Box - Left Card */}
              <Card size="small" title="Radar Bounding Box" style={{ width: 280, flexShrink: 0 }}>
                <div style={{ marginBottom: 8, fontSize: '12px', color: '#666' }}>
                  Define the geographic bounding box for radar data coverage.
                </div>
                <Form.Item label="Min Latitude" style={{ marginBottom: 8 }}>
                  <InputNumber
                    value={radarBbox?.minLat ?? undefined}
                    onChange={(value) => {
                      if (value !== null) {
                        setRadarBbox(prev => ({
                          minLon: prev?.minLon ?? 0,
                          maxLon: prev?.maxLon ?? 0,
                          minLat: value,
                          maxLat: prev?.maxLat ?? 0,
                        }));
                      }
                    }}
                    step={0.0001}
                    precision={6}
                    style={{ width: '100%' }}
                    placeholder="e.g. -23.7"
                  />
                </Form.Item>
                <Form.Item label="Max Latitude" style={{ marginBottom: 8 }}>
                  <InputNumber
                    value={radarBbox?.maxLat ?? undefined}
                    onChange={(value) => {
                      if (value !== null) {
                        setRadarBbox(prev => ({
                          minLon: prev?.minLon ?? 0,
                          maxLon: prev?.maxLon ?? 0,
                          minLat: prev?.minLat ?? 0,
                          maxLat: value,
                        }));
                      }
                    }}
                    step={0.0001}
                    precision={6}
                    style={{ width: '100%' }}
                    placeholder="e.g. -23.5"
                  />
                </Form.Item>
                <Form.Item label="Min Longitude" style={{ marginBottom: 8 }}>
                  <InputNumber
                    value={radarBbox?.minLon ?? undefined}
                    onChange={(value) => {
                      if (value !== null) {
                        setRadarBbox(prev => ({
                          minLon: value,
                          maxLon: prev?.maxLon ?? 0,
                          minLat: prev?.minLat ?? 0,
                          maxLat: prev?.maxLat ?? 0,
                        }));
                      }
                    }}
                    step={0.0001}
                    precision={6}
                    style={{ width: '100%' }}
                    placeholder="e.g. -46.8"
                  />
                </Form.Item>
                <Form.Item label="Max Longitude" style={{ marginBottom: 8 }}>
                  <InputNumber
                    value={radarBbox?.maxLon ?? undefined}
                    onChange={(value) => {
                      if (value !== null) {
                        setRadarBbox(prev => ({
                          minLon: prev?.minLon ?? 0,
                          maxLon: value,
                          minLat: prev?.minLat ?? 0,
                          maxLat: prev?.maxLat ?? 0,
                        }));
                      }
                    }}
                    step={0.0001}
                    precision={6}
                    style={{ width: '100%' }}
                    placeholder="e.g. -46.6"
                  />
                </Form.Item>
                {radarBbox && (
                  <Button 
                    type="link" 
                    danger 
                    size="small" 
                    onClick={() => setRadarBbox(null)}
                    style={{ padding: 0 }}
                  >
                    Clear bounding box
                  </Button>
                )}
              </Card>
              
              {/* Radar Coverage Periods - Right Card */}
              <Card size="small" title="Radar Coverage Periods" style={{ flex: 1 }}>
                <Table
                  dataSource={radarPeriods}
                  columns={radarPeriodColumns}
                  pagination={false}
                  scroll={{ x: 490 }}
                  size="small"
                  locale={{ emptyText: 'No radar periods yet. Click the + button below to create one.' }}
                />
                <Button
                  type="dashed"
                  onClick={handleAddRadarPeriod}
                  style={{ width: '100%', marginTop: 16 }}
                  icon={<PlusOutlined />}
                />
              </Card>
            </div>
          </div>
        );
      
      case 6:
        return (
          <Spin spinning={calculatingJobs} tip="Calculating job schedules...">
          <Form form={form} layout="vertical">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
              <Alert
                title="Job Scheduling"
                description="Configure which automated jobs should be created for this event. All jobs will be scheduled in the circuit's timezone and stored in UTC. You can view and manage these jobs later in the Jobs page."
                type="info"
                showIcon
                style={{ flex: 1, padding: '8px 12px', minWidth: 0 }}
              />
              <Button
                icon={<EyeOutlined />}
                onClick={() => setIsJobsPreviewModalOpen(true)}
                disabled={calculatingJobs || !dateRange || sessions.length === 0 || !jobCounts || jobCounts.total === 0}
                style={{ flexShrink: 0 }}
              >
                Preview Timeline
              </Button>
            </div>
            
            <Space orientation="vertical" size="small" style={{ width: '100%' }}>
              {/* Row 1: Weather Messages and Daily Forecast */}
              <div style={{ display: 'flex', gap: 12 }}>
                <Card size="small" style={{ flex: 1, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Weather Messages</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Temperature and rain probability alerts before sessions. All sessions get the same schedule: temperature at 3 hours before, temperature at 1 hour before, rain probability at 1 hour before, and temperature at 15 minutes before.
                      </div>
                    </div>
                    <Switch
                      checked={enableWeatherMessages}
                      onChange={setEnableWeatherMessages}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableWeatherMessages && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.weatherMessages} jobs will be created
                    </div>
                  )}
                </Card>
                
                <Card size="small" style={{ flex: 1, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Daily Forecast Jobs</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Daily forecast generation from event start until day before first session. Jobs run every 4 hours (00:00, 04:00, 08:00, 12:00, 16:00, 20:00) in circuit timezone.
                      </div>
                    </div>
                    <Switch
                      checked={enableDailyForecast}
                      onChange={setEnableDailyForecast}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableDailyForecast && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.dailyForecast} jobs will be created
                    </div>
                  )}
                </Card>
              </div>
              
              {/* Row 2: Hourly Forecast, Backup, Pipeline Status Checks */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Card size="small" style={{ flex: 1, minWidth: 280, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Hourly Forecast Jobs</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Hourly forecast generation from day before first session until end of race. Jobs run every 4 hours in circuit timezone.
                      </div>
                    </div>
                    <Switch
                      checked={enableHourlyForecast}
                      onChange={setEnableHourlyForecast}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableHourlyForecast && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.hourlyForecast} jobs will be created
                    </div>
                  )}
                </Card>

                <Card size="small" style={{ flex: 1, minWidth: 280, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Minutely Forecast Jobs</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        30-minute minutely forecasts from 30 minutes before each session until session end. Jobs run every 5 minutes. Not available for legacy events.
                      </div>
                    </div>
                    <Switch
                      checked={enableMinutelyForecast}
                      onChange={setEnableMinutelyForecast}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableMinutelyForecast && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.minutelyForecast} jobs will be created
                    </div>
                  )}
                </Card>
                
                <Card size="small" style={{ flex: 1, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Backup Jobs</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Daily BigQuery table backups. One job per day at end of day (23:59:59) in circuit timezone, for all days of the event.
                      </div>
                    </div>
                    <Switch
                      checked={enableBackup}
                      onChange={setEnableBackup}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableBackup && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.backup} jobs will be created
                    </div>
                  )}
                </Card>

                <Card size="small" style={{ flex: 1, minWidth: 280, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Pipeline Status Checks</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Stations and radar pipeline monitoring. Every 15 min during event, every 5 min during sessions. No jobs after last session.
                      </div>
                    </div>
                    <Switch
                      checked={enablePipelineStatusChecks}
                      onChange={setEnablePipelineStatusChecks}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {enablePipelineStatusChecks && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: '11px', color: '#666', marginBottom: 4 }}>
                        Monitor jobs start from ({circuitTimezone})
                      </div>
                      <DatePicker
                        showTime
                        value={pipelineMonitorStart ? dayjs(pipelineMonitorStart.format('YYYY-MM-DD HH:mm:ss')) : null}
                        onChange={(date) => setPipelineMonitorStart(date ? dayjs.tz(date.format('YYYY-MM-DD HH:mm:ss'), circuitTimezone) : null)}
                        format="YYYY-MM-DD HH:mm"
                        placeholder="Day 3, 16:00 (default)"
                        style={{ width: '100%' }}
                        showNow={false}
                      />
                    </div>
                  )}
                  {jobCounts && enablePipelineStatusChecks && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.stationsStatusCheck} stations + {jobCounts.radarStatusCheck} radar jobs will be created
                    </div>
                  )}
                </Card>
              </div>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Card size="small" style={{ flex: 1, minWidth: 280, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Archive Observations</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Archives station observations to GCS after the event ends. Scheduled for end of last day at 23:59:59.
                      </div>
                    </div>
                    <Switch
                      checked={enableArchiveObservations}
                      onChange={setEnableArchiveObservations}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableArchiveObservations && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.archiveObservations} job will be created
                    </div>
                  )}
                </Card>
              </div>
              
              {/* Summary */}
              {jobCounts && jobCounts.total > 0 && (
                <Card size="small" style={{ backgroundColor: '#f0f9ff', borderColor: '#1890ff', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Total Jobs to Create</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        All jobs will be created after the event is successfully created. You can view and manage them in the Jobs page.
                      </div>
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1890ff', marginLeft: 16 }}>
                      {jobCounts.total}
                    </div>
                  </div>
                </Card>
              )}
            </Space>
          </Form>
          </Spin>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
    <Modal
      title="Create New Event"
      open={open}
      onCancel={onClose}
      width={1200}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      keyboard={false}
      centered
      styles={{
        body: {
          height: 760,
          padding: '24px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Steps current={currentStep} items={steps} style={{ marginBottom: 24, flexShrink: 0 }} />
      
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        overflowX: 'hidden',
        paddingRight: 8,
        marginRight: -8,
      }}>
        {renderStepContent()}
      </div>
      
      <Divider style={{ margin: '16px 0', flexShrink: 0 }} />
      
      <Space style={{ width: '100%', justifyContent: 'space-between', flexShrink: 0 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Space>
          {currentStep > 0 && (
            <Button onClick={handlePrevious}>Previous</Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={handleNext}>
              Next
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <Button type="primary" onClick={handleCreate} loading={loading}>
              Create Event
            </Button>
          )}
        </Space>
      </Space>
    </Modal>

    {/* Jobs Preview Timeline Modal */}
    <Modal
      title="Jobs Timeline Preview"
      open={isJobsPreviewModalOpen}
      onCancel={() => setIsJobsPreviewModalOpen(false)}
      footer={
        <Button onClick={() => setIsJobsPreviewModalOpen(false)}>
          Close
        </Button>
      }
      width={1600}
      destroyOnHidden
    >
      {dateRange && sessions.length > 0 && previewJobs.length > 0 ? (
        <div style={{ marginTop: 8 }}>
          <Alert
            title="Preview of jobs to be created"
            description="This timeline shows when each job will run. Session bars indicate event sessions (FP1, SQ, SPRINT, Q, RACE, etc.). All jobs will be created after the event is successfully created."
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
          />
          <JobsTimeline
            jobs={previewJobs}
            sessions={sessions}
            eventStartDate={dateRange[0].format('YYYY-MM-DD')}
            eventEndDate={dateRange[1].format('YYYY-MM-DD')}
            circuitTimeZone={circuitTimezone}
            loading={false}
          />
        </div>
      ) : (
        <Alert
          title="No jobs to preview"
          description="Configure sessions and enable at least one job type to see the timeline preview."
          type="warning"
          showIcon
        />
      )}
    </Modal>
    </>
  );
};

